import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar } from '../Avatar'

export interface AvatarStackItem {
  key: string
  label?: string
  src?: string
  color?: string
}

export interface AvatarStackProps extends Omit<BoxProps, 'children'> {
  items: AvatarStackItem[]
  max?: number
  size?: number
}

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
})

const StackedAvatar = styled(Avatar, {
  shouldForwardProp: (p) => p !== 'offset',
})<{ offset: number }>(({ offset }) => ({
  marginLeft: offset === 0 ? 0 : -10,
  border: '2px solid var(--ds-canvas)',
  boxShadow: '0 0 0 1px var(--ds-border)',
  zIndex: 100 - offset,
}))

const Overflow = styled(Box)({
  marginLeft: -10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  background: 'var(--ds-hover)',
  color: 'var(--ds-ink-muted)',
  border: '2px solid var(--ds-canvas)',
  borderRadius: '50%',
})

/** Overlapping avatar stack with "+N" overflow — presence rows, guest lists. */
export function AvatarStack({ items, max = 4, size = 30, ...rest }: AvatarStackProps) {
  const visible = items.slice(0, max)
  const overflow = items.length - visible.length

  return (
    <Row {...rest}>
      {visible.map((item, i) => (
        <StackedAvatar
          key={item.key}
          offset={i}
          src={item.src}
          sx={{ width: size, height: size, fontSize: size * 0.4, bgcolor: item.color }}
        >
          {!item.src && (item.label?.[0]?.toUpperCase() ?? '?')}
        </StackedAvatar>
      ))}
      {overflow > 0 && (
        <Overflow sx={{ width: size, height: size, fontSize: size * 0.36 }}>+{overflow}</Overflow>
      )}
    </Row>
  )
}
